import { Avatar, Typography } from "@/components/atoms";
import { ClientPaymentMethod } from "@/redux/apis/clientPayment/types";
import { PointsOfInterest } from "@/redux/apis/clientPoi/types";
import { DetailRow } from "../summary/DetailRow";
import { PaymentMethodRow } from "../summary/PaymentMethodRow";
import { RouteTimeline } from "../summary/RouteTimeline";
import { boxState, SummaryBox } from "../summary/SummaryBox";
import { TripVehicleRow } from "../summary/TripVehicleRow";

/** An extra passenger row (#2..N) on the confirmation. */
export type ConfirmationExtraPassenger = { name: string; phone?: string };

/**
 * Everything the confirmation's trip boxes render — a plain, serialisable snapshot captured at
 * submit time (the draft is cleared right after), so it survives router-state navigation with no
 * store reads. Mirrors what the Booking Summary panel shows on the Payment step.
 */
export type TripDetails = {
  passenger: { name: string; email?: string; phone?: string; avatarSrc?: string };
  /** Passenger 1 as a labelled "(Passenger 1)" row (raw name/phone) — mirrors the Booking Summary panel. */
  mainPassenger?: { name: string; phone?: string };
  /** Ordered `[pickup, ...stops, destination]` — the route timeline. */
  locations: PointsOfInterest[];
  /** The arrival flight, shown under the pickup (DSN-689) — captured with the draft for the same reason as the rest. */
  pickupFlightNumber?: string | null;
  pickupNotFlying?: boolean;
  /** The departure flight, shown under the drop-off (DSN-689). */
  departureFlightNumber?: string | null;
  date: string;
  time?: string;
  /**
   * Pre-formatted pickup-delay caption ("incl. 20 min driver delay") — mirrors the Booking Summary's
   * date row, where it rides beside the time rather than in an Airplane row of its own.
   */
  delayCaption?: string;
  vehicleLabel: string;
  vehicleType?: string;
  /** Formatted duration ("2 hours") for hourly/daily rates — mirrors the Booking Summary. */
  duration?: string;
  /** @deprecated No longer shown: Trip details has no price override line (DSN-682). */
  fixedRate?: string;
  vehicleExtras?: string;
  // Other Details — each renders only when present.
  bookedBy?: string;
  reference?: string;
  additionalInfo?: string;
  attachments?: string[];
  extraPassengers?: ConfirmationExtraPassenger[];
  /** Payment box — authed carries the saved method (full row); a guest carries display text only. */
  paymentMethod?: ClientPaymentMethod | null;
  card?: { label: string; expiry?: string };
};

/** Passenger row — mirrors the Booking Summary's PassengerRow (avatar + name/email/phone). */
const PassengerRow = ({ passenger }: { passenger: TripDetails["passenger"] }) => (
  <div className="flex items-start gap-2.5">
    <Avatar size="xs" src={passenger.avatarSrc} alt={passenger.name} />
    <div className="flex min-w-0 flex-col gap-0.5">
      <Typography variant="small" className="block truncate">
        {passenger.name}
      </Typography>
      {passenger.email && (
        <Typography variant="small-regular" className="block truncate text-neutral-dark-gray">
          {passenger.email}
        </Typography>
      )}
      {passenger.phone && (
        <Typography variant="small-regular" className="block text-neutral-dark-gray">
          {passenger.phone}
        </Typography>
      )}
    </div>
  </div>
);

/**
 * Left column of the confirmation card — the same three boxes the Booking Summary panel shows
 * (Address / Trip Details / Payment), rendered from a serialisable snapshot rather than the draft.
 */
export const TripDetailsCard = ({ trip }: { trip: TripDetails }) => {
  const attachments = (trip.attachments ?? []).filter(Boolean);
  const extraPassengers = (trip.extraPassengers ?? []).filter((p) => p.name.trim());
  const hasOtherDetails = !!(trip.bookedBy || trip.reference || trip.additionalInfo || attachments.length || trip.mainPassenger?.name || extraPassengers.length);
  const hasPayment = !!(trip.paymentMethod || trip.card);

  return (
    <div className="flex flex-col gap-2">
      <SummaryBox icon="Location" title="Address" state={boxState(false, true)}>
        <RouteTimeline
          locations={trip.locations}
          pickupFlightNumber={trip.pickupFlightNumber}
          pickupNotFlying={trip.pickupNotFlying}
          departureFlightNumber={trip.departureFlightNumber}
        />
      </SummaryBox>

      <SummaryBox icon="User" title="Trip Details" state={boxState(false, true)}>
        <div className="flex flex-col gap-4">
          <PassengerRow passenger={trip.passenger} />
          {trip.date && (
            <DetailRow
              icon="Calendar"
              title={trip.date}
              subtitle={trip.time}
              subtitleTrailing={
                trip.delayCaption ? (
                  <Typography variant="caption" className="whitespace-nowrap text-info">
                    {trip.delayCaption}
                  </Typography>
                ) : undefined
              }
            />
          )}
          {/* Shared with the Booking Summary's Trip Details box so the two never drift. */}
          {trip.vehicleLabel && (
            <TripVehicleRow
              transferLabel={trip.vehicleLabel}
              vehicleType={trip.vehicleType}
              duration={trip.duration}
              extras={trip.vehicleExtras}
            />
          )}
          {/* The confirmation box is never the active step, so the divider is neutral (not primary) —
              matching TripDetailsBody's non-highlighted state in the Booking Summary. */}
          {hasOtherDetails && <div className="h-px bg-neutral-mid-gray" />}
          {trip.bookedBy && <DetailRow icon="User" title="Booked by" subtitle={trip.bookedBy} />}
          {trip.reference && <DetailRow icon="NoteText" title="Reference Number" subtitle={trip.reference} />}
          {trip.additionalInfo && <DetailRow icon="NoteText" title="Additional notes to your driver" subtitle={trip.additionalInfo} />}
          {attachments.map((name, index) => (
            <DetailRow key={`attachment-${index}`} icon="DocumentText" title="Attachments" subtitle={name} />
          ))}
          {trip.mainPassenger?.name && (
            <DetailRow icon="Profile2User" title={`${trip.mainPassenger.name} (Passenger 1)`} subtitle={trip.mainPassenger.phone} />
          )}
          {extraPassengers.map((passenger, index) => (
            <DetailRow
              key={`passenger-${index}`}
              icon="Profile2User"
              title={`${passenger.name} (Passenger ${index + 2})`}
              subtitle={passenger.phone}
            />
          ))}
        </div>
      </SummaryBox>

      {hasPayment && (
        <SummaryBox icon="Card" title="Payment" state={boxState(false, true)}>
          {trip.paymentMethod ? (
            <PaymentMethodRow method={trip.paymentMethod} />
          ) : (
            trip.card && <DetailRow icon="Card" title={trip.card.label} subtitle={trip.card.expiry} />
          )}
        </SummaryBox>
      )}
    </div>
  );
};
